import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { useTheme } from 'contexts/ThemeContext';

import { PML } from 'utils/rest';

import { Text } from 'components/DataDisplay';
import { Button, IconButton } from 'components/Inputs';
import { Container, Item, Row } from 'components/Layout';
import { Loading } from 'components/Loading';

const PREVIEW_SLICE = 5;
const CARD_WIDTH = 720;
const STAGE_HEIGHT = 440;
const STAGE_BOX_WIDTH = 460;
const SWIPE_DISTANCE = 60;
const SWIPE_COOLDOWN = 400;
const SLIDE_OFFSET = 48;
const SLIDE_MS = 260;
const MAX_DOTS = 15;

const FORMATS = {
  META_FEED: { label: 'Feed', width: 1080, height: 1080 },
  META_STORY: { label: 'Story', width: 1080, height: 1920 }
};

const slices = (items, size) => {
  const groups = [];

  for (let index = 0; index < items.length; index += size) {
    groups.push(items.slice(index, index + size));
  }

  return groups;
};

const previewKey = preview => `${preview.creaKey}|${preview.format}`;

const formatLabel = format => FORMATS[format]?.label || format;

const formatSize = format => {
  const known = FORMATS[format];

  return known ? `${known.width} × ${known.height}` : '';
};

const stageWidth = format => {
  const known = FORMATS[format];

  return known
    ? Math.round((STAGE_HEIGHT * known.width) / known.height)
    : STAGE_HEIGHT;
};

const toPreviewUrl = video => {
  const bytes = Uint8Array.from(atob(video), character =>
    character.charCodeAt(0)
  );

  return window.URL.createObjectURL(new Blob([bytes], { type: 'video/mp4' }));
};

const downloadPreview = (preview, url) => {
  const link = document.createElement('a');

  link.href = url;
  link.setAttribute('download', `${preview.creaKey}_${preview.format}.mp4`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const adSubtitle = ad => {
  const campaigns = ad.campaignKeys.join(', ');

  if (!campaigns) {
    return ad.gabarit || '';
  }

  return ad.gabarit ? `${campaigns} · ${ad.gabarit}` : campaigns;
};

const groupByAd = previews => {
  const ads = [];
  const positionByCreaKey = new Map();

  previews.forEach(preview => {
    const position = positionByCreaKey.get(preview.creaKey);

    if (position === undefined) {
      positionByCreaKey.set(preview.creaKey, ads.length);
      ads.push({
        creaKey: preview.creaKey,
        name: preview.name,
        gabarit: preview.gabarit,
        campaignKeys: preview.campaignKeys || [],
        formats: [preview]
      });
      return;
    }

    ads[position].formats.push(preview);
  });

  return ads;
};

const pickPreview = (ad, format) =>
  ad.formats.find(preview => preview.format === format) || ad.formats[0];

const PreviewPlayer = ({ ad, format, url, accentColor, onFormatChange }) => {
  const { themeColors } = useTheme();
  const preview = pickPreview(ad, format);

  const renderStage = () => {
    if (url === undefined) {
      return <Loading loading color={accentColor} />;
    }

    if (url === null) {
      return (
        <Text fontSize="12px" color="lightSmooth">
          Aperçu indisponible pour ce format.
        </Text>
      );
    }

    return (
      <video
        key={previewKey(preview)}
        src={url}
        controls
        muted
        playsInline
        preload="metadata"
        style={{ width: '100%', height: '100%', display: 'block' }}
      />
    );
  };

  return (
    <Container spacing={0} style={{ width: `${STAGE_BOX_WIDTH}px` }}>
      <Row spacing={0}>
        <Item
          flex
          justify="center"
          alignItems="center"
          style={{ width: '100%', height: `${STAGE_HEIGHT}px` }}
        >
          <Item
            flex
            justify="center"
            alignItems="center"
            style={{
              width: `${stageWidth(preview.format)}px`,
              height: '100%',
              borderRadius: '10px',
              backgroundColor: themeColors.tertiary,
              overflow: 'hidden'
            }}
          >
            {renderStage()}
          </Item>
        </Item>
      </Row>
      <Row spacing={0} style={{ paddingTop: '14px' }}>
        {ad.formats.map(({ format: key }) => (
          <Item key={key} flex style={{ padding: '0px 4px' }}>
            <Button
              variant={key === preview.format ? 'contained' : 'outlined'}
              color={accentColor}
              size="small"
              rounded
              onClick={() => onFormatChange(key)}
            >
              {formatLabel(key)}
            </Button>
          </Item>
        ))}
      </Row>
      <Row spacing={0} style={{ paddingTop: '6px' }}>
        <Item flex>
          <Text fontSize="11px" color="lightSmooth">
            {formatSize(preview.format)}
            {preview.shopKey ? ` · exemple ${preview.shopKey}` : ''}
          </Text>
        </Item>
      </Row>
    </Container>
  );
};

PreviewPlayer.defaultProps = {
  url: undefined
};

PreviewPlayer.propTypes = {
  ad: PropTypes.objectOf(PropTypes.any).isRequired,
  format: PropTypes.string.isRequired,
  url: PropTypes.string,
  accentColor: PropTypes.string.isRequired,
  onFormatChange: PropTypes.func.isRequired
};

const PreviewGallery = ({ previews, accentColor }) => {
  const { themeColors } = useTheme();
  const ads = useMemo(() => groupByAd(previews), [previews]);
  const [position, setPosition] = useState(0);
  const [format, setFormat] = useState('');
  const [urlByKey, setUrlByKey] = useState({});
  const [slideFrom, setSlideFrom] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const urlsRef = useRef({});
  const requestedRef = useRef(new Set());
  const dragStartRef = useRef(null);
  const lastSwipeRef = useRef(0);
  const stripRef = useRef(null);

  const current = Math.min(position, Math.max(ads.length - 1, 0));
  const currentAd = ads[current];
  const currentPreview = currentAd ? pickPreview(currentAd, format) : null;
  const currentKey = currentPreview ? previewKey(currentPreview) : '';

  const loadPreviews = async wanted => {
    const missing = wanted.filter(
      preview => !requestedRef.current.has(previewKey(preview))
    );

    if (!missing.length) {
      return urlsRef.current;
    }

    missing.forEach(preview => requestedRef.current.add(previewKey(preview)));

    for (const slice of slices(missing, PREVIEW_SLICE)) {
      // eslint-disable-next-line no-await-in-loop
      const fetched = await PML.getVideoPreviews(
        slice.map(({ creaKey, format: key, shopKey }) => ({
          promo: creaKey,
          format: key,
          shopKey
        }))
      );
      const fetchedByKey = {};

      fetched.forEach(({ promo, format: key, video }) => {
        fetchedByKey[`${promo}|${key}`] = toPreviewUrl(video);
      });

      slice.forEach(preview => {
        urlsRef.current[previewKey(preview)] =
          fetchedByKey[previewKey(preview)] || null;
      });

      setUrlByKey({ ...urlsRef.current });
    }

    return urlsRef.current;
  };

  const neighbourPreviews = () => {
    const around = [ads[current - 1], ads[current + 1]].reduce(
      (acc, ad) => (ad ? [...acc, pickPreview(ad, format)] : acc),
      []
    );

    return [...currentAd.formats, ...around];
  };

  useEffect(() => {
    let dropped = false;

    const load = async () => {
      if (!currentPreview) {
        return;
      }

      await loadPreviews([currentPreview]);

      if (!dropped) {
        await loadPreviews(neighbourPreviews());
      }
    };

    load();

    return () => {
      dropped = true;
    };
    // eslint-disable-next-line
  }, [currentKey]);

  useEffect(() => {
    if (!slideFrom) {
      return undefined;
    }

    let settle = 0;
    const paint = window.requestAnimationFrame(() => {
      settle = window.requestAnimationFrame(() => setSlideFrom(0));
    });
    const guard = window.setTimeout(() => setSlideFrom(0), SLIDE_MS);

    return () => {
      window.cancelAnimationFrame(paint);
      window.cancelAnimationFrame(settle);
      window.clearTimeout(guard);
    };
  }, [slideFrom]);

  useEffect(() => {
    const strip = stripRef.current;

    if (!strip) {
      return undefined;
    }

    const holdGesture = event => {
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
        event.preventDefault();
      }
    };

    strip.addEventListener('wheel', holdGesture, { passive: false });

    return () => strip.removeEventListener('wheel', holdGesture);
  }, []);

  useEffect(() => {
    const urls = urlsRef.current;

    return () => {
      Object.values(urls).forEach(
        url => url && window.URL.revokeObjectURL(url)
      );
    };
  }, []);

  const goTo = wanted => {
    const next = Math.min(Math.max(wanted, 0), ads.length - 1);

    if (next !== current) {
      setSlideFrom(next > current ? SLIDE_OFFSET : -SLIDE_OFFSET);
      setPosition(next);
    }
  };

  const swipe = distance => {
    const now = Date.now();

    if (now - lastSwipeRef.current < SWIPE_COOLDOWN) {
      return;
    }

    lastSwipeRef.current = now;
    goTo(current + (distance < 0 ? 1 : -1));
  };

  const onPointerDown = event => {
    dragStartRef.current =
      event.target.tagName === 'VIDEO' ? null : event.clientX;
  };

  const onPointerUp = event => {
    const start = dragStartRef.current;

    dragStartRef.current = null;

    if (start === null) {
      return;
    }

    const distance = event.clientX - start;

    if (Math.abs(distance) >= SWIPE_DISTANCE) {
      swipe(distance);
    }
  };

  const onWheel = event => {
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY) + 4) {
      swipe(-event.deltaX);
    }
  };

  const onKeyDown = event => {
    if (event.key === 'ArrowLeft') {
      goTo(current - 1);
    }

    if (event.key === 'ArrowRight') {
      goTo(current + 1);
    }
  };

  const onDownload = () => {
    const url = urlByKey[currentKey];

    if (url) {
      downloadPreview(currentPreview, url);
    }
  };

  const onDownloadAll = async () => {
    const everyPreview = ads.reduce((acc, ad) => [...acc, ...ad.formats], []);

    setDownloading(true);
    const urls = await loadPreviews(everyPreview);

    setDownloading(false);
    everyPreview.forEach(preview => {
      const url = urls[previewKey(preview)];

      if (url) {
        downloadPreview(preview, url);
      }
    });
  };

  if (!ads.length) {
    return (
      <Text fontSize="12px">Aucun aperçu disponible pour cet import.</Text>
    );
  }

  const arrowStyle = { visibility: ads.length > 1 ? 'visible' : 'hidden' };

  const slideStyle = {
    transform: `translateX(${slideFrom}px)`,
    opacity: slideFrom ? 0 : 1,
    transition: slideFrom
      ? 'none'
      : `transform ${SLIDE_MS}ms ease, opacity ${SLIDE_MS}ms ease`
  };

  return (
    <Container spacing={0}>
      <Item
        xs={12}
        style={{
          maxWidth: `${CARD_WIDTH}px`,
          padding: '16px 20px',
          borderRadius: '12px',
          border: `1px solid ${themeColors.greyLight}`,
          backgroundColor: themeColors.secondary
        }}
      >
        <Row spacing={0} alignItems="center">
          <Item
            xs
            justify="flex-start"
            style={{ flexDirection: 'column', ...slideStyle }}
          >
            <Item xs justify="flex-start">
              <Text fontSize="14px" fontWeight={700} noWrap>
                {currentAd.name || currentAd.creaKey}
              </Text>
            </Item>
            <Item xs justify="flex-start">
              <Text fontSize="11px" color="lightSmooth" noWrap>
                {adSubtitle(currentAd)}
              </Text>
            </Item>
          </Item>
          <Item flex justify="flex-end" style={{ gap: '10px' }}>
            <Text fontSize="11px" color="lightSmooth" noWrap>
              Publicité {current + 1} / {ads.length}
            </Text>
            {!!urlByKey[currentKey] && (
              <Button
                variant="outlined"
                color={accentColor}
                size="small"
                rounded
                onClick={onDownload}
              >
                TÉLÉCHARGER
              </Button>
            )}
            <Button
              variant="outlined"
              color={accentColor}
              size="small"
              rounded
              loading={downloading}
              onClick={onDownloadAll}
            >
              TÉLÉCHARGER TOUT
            </Button>
          </Item>
        </Row>
        <Row
          ref={stripRef}
          spacing={0}
          alignItems="center"
          style={{ paddingTop: '12px', outline: 'none' }}
          tabIndex={0}
          onKeyDown={onKeyDown}
          onWheel={onWheel}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
        >
          <Item flex style={arrowStyle}>
            <IconButton
              color={accentColor}
              disabled={current === 0}
              onClick={() => goTo(current - 1)}
              aria-label="Publicité précédente"
            >
              <ChevronLeftIcon />
            </IconButton>
          </Item>
          <Item flex style={slideStyle}>
            <PreviewPlayer
              ad={currentAd}
              format={currentPreview.format}
              url={urlByKey[currentKey]}
              accentColor={accentColor}
              onFormatChange={setFormat}
            />
          </Item>
          <Item flex style={arrowStyle}>
            <IconButton
              color={accentColor}
              disabled={current === ads.length - 1}
              onClick={() => goTo(current + 1)}
              aria-label="Publicité suivante"
            >
              <ChevronRightIcon />
            </IconButton>
          </Item>
        </Row>
        {ads.length > 1 && ads.length <= MAX_DOTS && (
          <Row spacing={0} style={{ paddingTop: '14px' }}>
            {ads.map((ad, dot) => (
              <Item
                key={ad.creaKey}
                flex
                onClick={() => goTo(dot)}
                style={{
                  width: '8px',
                  height: '8px',
                  margin: '0px 4px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  backgroundColor:
                    dot === current ? accentColor : themeColors.greyLight
                }}
              />
            ))}
          </Row>
        )}
      </Item>
    </Container>
  );
};

PreviewGallery.propTypes = {
  previews: PropTypes.arrayOf(PropTypes.object).isRequired,
  accentColor: PropTypes.string.isRequired
};

export default PreviewGallery;
